import {
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Box,
  alpha,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useMapLayersState } from 'state/useMapLayersState';
import {
  DiagonalHatchPatternBlack,
  FIRISPerimeter,
  NIFCPerimeter,
  ReportIcon,
  PowerOutageBackground,
  WeatherStationIcon,
  FloodWarningPattern,
  FloodWatchPattern,
  RedFlagWarningPattern,
  FireWeatherPattern,
} from 'features/mapping/icons';
import { MapLayers } from 'features/mapping/mapping.constants';
import { LegendListItem } from './LegendListItem';
import { LegendSectionTitle } from './LegendSectionTitle';
import { LegendListItemText } from './LegendListItemText';
import {
  FLOOD_COLOR,
  FLOOD_WATCH_FILL_COLOR,
  NWS_RED_FLAG_WARNING_FILL_COLOR,
  NWS_FIRE_WEATHER_WATCH_FILL_COLOR,
  NWS_FIRE_ALERT_FILL_OPACITY_STREET,
  NWS_ALERT_PATTERN_FILL_OPACITY_ASSOCIATED,
  NWS_RED_FLAG_WARNING_STROKE_COLOR_STREET,
  NWS_FIRE_WEATHER_WATCH_STROKE_COLOR_STREET,
  NWS_FLOOD_WARNING_FILL_OPACITY_STREET,
  NWS_FLOOD_WATCH_FILL_OPACITY_STREET,
  NWS_FLOOD_STROKE_COLOR_STREET,
} from '../layers/NWSWatchesAndWarningsLayer/NWSWatchesAndWarningsLayer.constants';

const patternAvatarBoxStyle = {
  width: '100%',
  height: '100%',
};

const useStyles = makeStyles()(() => ({
  noCoverageIcon: {
    ...patternAvatarBoxStyle,
    backgroundColor: '#cccccc',
    maskImage: `url("${DiagonalHatchPatternBlack.data}")`,
    maskSize: `${DiagonalHatchPatternBlack.width}px ${DiagonalHatchPatternBlack.height}px`,
  },
  firisPerimeterIcon: {
    ...patternAvatarBoxStyle,
    backgroundColor: '#FD2D2C40',
    position: 'relative',
    '::after': {
      content: '" "',
      width: '60%',
      height: '60%',
      backgroundImage: `url("${FIRISPerimeter.data}")`,
      filter: 'opacity(60%)',
      border: '1.5px dashed #FD2D2C',
      position: 'absolute',
      top: '20%',
      left: '20%',
    },
  },
  nifcPerimeterIcon: {
    ...patternAvatarBoxStyle,
    backgroundColor: '#FD2D2C',
    background: `url("${NIFCPerimeter.data}")`,
    backgroundSize: '100% 100%',
  },
  redFlagWarningsIcon: {
    ...patternAvatarBoxStyle,
    position: 'relative',
    backgroundColor: alpha(
      NWS_RED_FLAG_WARNING_FILL_COLOR,
      NWS_FIRE_ALERT_FILL_OPACITY_STREET,
    ),
    border: `1.5px dashed ${NWS_RED_FLAG_WARNING_STROKE_COLOR_STREET}`,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `url("${RedFlagWarningPattern.data}")`,
      backgroundSize: '40%',
      backgroundRepeat: 'repeat',
      backgroundPosition: 'center',
      opacity: NWS_ALERT_PATTERN_FILL_OPACITY_ASSOCIATED,
      pointerEvents: 'none',
    },
  },
  fireWeatherWatchIcon: {
    ...patternAvatarBoxStyle,
    position: 'relative',
    backgroundColor: alpha(
      NWS_FIRE_WEATHER_WATCH_FILL_COLOR,
      NWS_FIRE_ALERT_FILL_OPACITY_STREET,
    ),
    border: `1.5px dashed ${NWS_FIRE_WEATHER_WATCH_STROKE_COLOR_STREET}`,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `url("${FireWeatherPattern.data}")`,
      backgroundSize: '40%',
      backgroundRepeat: 'repeat',
      backgroundPosition: 'center',
      opacity: NWS_ALERT_PATTERN_FILL_OPACITY_ASSOCIATED,
      pointerEvents: 'none',
    },
  },
  powerOutagesIcon: {
    ...patternAvatarBoxStyle,
    backgroundColor: '#F7FB2DBF',
    background: `url("${PowerOutageBackground.data}")`,
    backgroundSize: 'cover',
    border: '2px solid #F7FB2DBF',
  },
  landManagmentIcon: {
    ...patternAvatarBoxStyle,
    background: 'rgba(252, 240, 180, 0.6)',
    border: '2px solid #FCF0B4',
  },
  bureauOfReclamationIcon: {
    ...patternAvatarBoxStyle,
    background: 'rgba(255, 255, 212, 0.6)',
    border: '2px solid #FFFFD4',
  },
  departmentOfDefenseIcon: {
    ...patternAvatarBoxStyle,
    background: 'rgba(245, 209, 223, 0.6)',
    border: '2px solid #F5D1DF',
  },
  fishWildlifeIcon: {
    ...patternAvatarBoxStyle,
    background: 'rgba(185, 221, 200, 0.6)',
    border: '2px solid #B9DDC8',
  },
  forestIcon: {
    ...patternAvatarBoxStyle,
    background: 'rgba(226, 242, 220, 0.6)',
    border: '2px solid #E2F2DC',
  },
  nationalParkIcon: {
    ...patternAvatarBoxStyle,
    background: 'rgba(220, 213, 231, 0.6)',
    border: '2px solid #DCD5E7',
  },
  electricRetailService: {
    ...patternAvatarBoxStyle,
    background: 'rgba(56, 133, 186, 0.6)',
    border: '2px solid rgba(56, 133, 186, 1)',
  },
  responsibilityAreasFRA: {
    ...patternAvatarBoxStyle,
    background: 'rgba(78, 7, 1, 0.5)',
    border: '2px solid rgba(78, 7, 1, 1)',
  },
  responsibilityAreasSRA: {
    ...patternAvatarBoxStyle,
    background: 'rgba(0, 40, 104, 0.6)',
    border: '2px solid rgba(0, 40, 104, 1)',
  },
  responsibilityAreasLRA: {
    ...patternAvatarBoxStyle,
    background: 'rgba(229, 160, 0, 0.5)',
    border: '2px solid rgba(229, 160, 0, 1)',
  },
  floodWarningIcon: {
    ...patternAvatarBoxStyle,
    position: 'relative',
    backgroundColor: alpha(FLOOD_COLOR, NWS_FLOOD_WARNING_FILL_OPACITY_STREET),
    border: `1.5px dashed ${NWS_FLOOD_STROKE_COLOR_STREET}`,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `url("${FloodWarningPattern.data}")`,
      backgroundSize: '40%',
      backgroundRepeat: 'repeat',
      backgroundPosition: 'center',
      opacity: NWS_ALERT_PATTERN_FILL_OPACITY_ASSOCIATED,
      pointerEvents: 'none',
    },
  },
  floodWatchIcon: {
    ...patternAvatarBoxStyle,
    position: 'relative',
    backgroundColor: alpha(
      FLOOD_WATCH_FILL_COLOR,
      NWS_FLOOD_WATCH_FILL_OPACITY_STREET,
    ),
    border: `1.5px dashed ${alpha(NWS_FLOOD_STROKE_COLOR_STREET, 0.6)}`,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `url("${FloodWatchPattern.data}")`,
      backgroundSize: '40%',
      backgroundRepeat: 'repeat',
      backgroundPosition: 'center',
      opacity: NWS_ALERT_PATTERN_FILL_OPACITY_ASSOCIATED,
      pointerEvents: 'none',
    },
  },
}));

export const MapCoverageLegend = (): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { mapLayers } = useMapLayersState();

  return (
    <List
      disablePadding
      dense
      subheader={
        <LegendSectionTitle>
          {t('map.legends.coverage.title')}
        </LegendSectionTitle>
      }
    >
      {mapLayers.includes(MapLayers.RED_FLAG_WARNINGS) && (
        <>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.redFlagWarningsIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.redFlagWarnings.title')}
              secondary={t('map.legends.coverage.redFlagWarnings.subtitle')}
            />
          </ListItem>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.fireWeatherWatchIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.fireWeatherWatch.title')}
              secondary={t('map.legends.coverage.fireWeatherWatch.subtitle')}
            />
          </ListItem>
        </>
      )}
      {mapLayers.includes(MapLayers.FLOOD_WEATHER) && (
        <>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.floodWarningIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.floodWarning.title')}
              secondary={t('map.legends.coverage.floodWarning.subtitle')}
            />
          </ListItem>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.floodWatchIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.floodWatch.title')}
              secondary={t('map.legends.coverage.floodWatch.subtitle')}
            />
          </ListItem>
        </>
      )}
      {mapLayers.includes(MapLayers.ACTIVE_FIRE_PERIMETERS) && (
        <>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.firisPerimeterIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.firisPerimeter.title')}
              secondary={t('map.legends.coverage.firisPerimeter.subtitle')}
            />
          </ListItem>

          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.nifcPerimeterIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.nifcPerimeter.title')}
              secondary={t('map.legends.coverage.nifcPerimeter.subtitle')}
            />
          </ListItem>
        </>
      )}
      {mapLayers.includes(MapLayers.POWER_OUTAGES) && (
        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.powerOutagesIcon} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t('map.legends.coverage.powerOutages.title')}
            secondary={t('map.legends.coverage.powerOutages.subtitle')}
          />
        </ListItem>
      )}
      <ListItem alignItems="flex-start">
        <ListItemAvatar>
          <Avatar variant="square">
            <Box className={classes.noCoverageIcon} />
          </Avatar>
        </ListItemAvatar>
        <LegendListItemText
          variant="watchduty-legend"
          primary={t('map.legends.coverage.noCoverage.title')}
          secondary={t('map.legends.coverage.noCoverage.subtitle')}
        />
      </ListItem>
      <LegendListItem
        iconSrc={ReportIcon.data}
        primaryText={t('map.legends.coverage.photo.title')}
        secondary={t('map.legends.coverage.photo.subtitle')}
      />
      {mapLayers.includes(MapLayers.FEDERAL_STATE_LOCAL) && (
        <>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.landManagmentIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.landManagment.title')}
              secondary={t('map.legends.coverage.landManagment.subtitle')}
            />
          </ListItem>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.bureauOfReclamationIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.bureauOfReclamation.title')}
              secondary={t('map.legends.coverage.bureauOfReclamation.subtitle')}
            />
          </ListItem>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.departmentOfDefenseIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.departmentOfDefense.title')}
              secondary={t('map.legends.coverage.departmentOfDefense.subtitle')}
            />
          </ListItem>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.fishWildlifeIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.fishWildlife.title')}
              secondary={t('map.legends.coverage.fishWildlife.subtitle')}
            />
          </ListItem>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.forestIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.forestService.title')}
              secondary={t('map.legends.coverage.forestService.subtitle')}
            />
          </ListItem>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.nationalParkIcon} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.nationalPark.title')}
              secondary={t('map.legends.coverage.nationalPark.subtitle')}
            />
          </ListItem>
        </>
      )}
      {mapLayers.includes(MapLayers.ELECTRIC_RETAIL_SERVICE) && (
        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.electricRetailService} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t('map.legends.coverage.electricRetailService.title')}
            secondary={t('map.legends.coverage.electricRetailService.subtitle')}
          />
        </ListItem>
      )}
      {mapLayers.includes(MapLayers.RESPONSIBILITY_AREAS) && (
        <>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.responsibilityAreasFRA} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.responsibilityAreasFRA.title')}
              secondary={t(
                'map.legends.coverage.responsibilityAreasFRA.subtitle',
              )}
            />
          </ListItem>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.responsibilityAreasSRA} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.responsibilityAreasSRA.title')}
              secondary={t(
                'map.legends.coverage.responsibilityAreasSRA.subtitle',
              )}
            />
          </ListItem>
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar variant="square">
                <Box className={classes.responsibilityAreasLRA} />
              </Avatar>
            </ListItemAvatar>
            <LegendListItemText
              variant="watchduty-legend"
              primary={t('map.legends.coverage.responsibilityAreasLRA.title')}
              secondary={t(
                'map.legends.coverage.responsibilityAreasLRA.subtitle',
              )}
            />
          </ListItem>
        </>
      )}
      {mapLayers.includes(MapLayers.WEATHER_STATIONS) && (
        <LegendListItem
          iconSrc={WeatherStationIcon.data}
          primaryText={t('map.legends.weatherStations.title')}
          secondary={t('map.legends.weatherStations.subtitle')}
          iconSize={WeatherStationIcon.width}
          avatarSxProps={{ rotate: '45deg' }}
        />
      )}
    </List>
  );
};
